import {FC, Fragment, useCallback, useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import {AppDispatch} from "../../../store";
import {useDispatch, useSelector} from "react-redux";
import {
    closeDiscardModel, delPayoutAddress,
    payoutAddressesSelector,
    setPayoutAddress
} from "../../../store/payout-addresses.store.ts";
import {useMutation} from "@apollo/client";
import NotificationToast from "../toasts/notification.toast.tsx";
import {NotificationType} from "../../enums/notification.enum.ts";
import {toast} from "react-toastify";
import {DiscardPayoutAddressResponseType} from "../../types/payment/discard-payout-address-response.type.ts";
import {DISCARD_PAYOUT_ADDRESS_MUTATION} from "../../../api/graphql/mutations/discard-payout-address.mutation.ts";


const DeletePayoutAddressModal: FC = () => {
    const dispatcher: AppDispatch = useDispatch();
    const [show, setShow] = useState<boolean>(false);
    const {discardModel: {addressId}, payoutAddress} = useSelector(payoutAddressesSelector);

    const [discardAddress, discardAddressData] = useMutation<DiscardPayoutAddressResponseType>(DISCARD_PAYOUT_ADDRESS_MUTATION);

    useEffect((): void => {
        if (!addressId) {
            setShow(false)
        } else {
            setShow(true)
        }

    }, [payoutAddress?.description, payoutAddress?.name, addressId]);

    useEffect((): void => {
        if (!show) {
            dispatcher(closeDiscardModel(null));
            dispatcher(setPayoutAddress(null));
        }
    }, [dispatcher, show]);

    useEffect(() => {
        if (discardAddressData.error) {
            toast(<NotificationToast type={NotificationType.ERROR} title="Payout Address" message={discardAddressData.error.message} />, {
                toastId: 'discard-address-error'
            });
        }
    }, [discardAddressData.error]);

    useEffect(() => {
        if (discardAddressData.data) {
            toast(<NotificationToast type={NotificationType.SUCCESS} title="Payout Address" message="Successfully deleted" />, {
                toastId: 'discard-address-success'
            });
            const response = discardAddressData.data.discardPayoutAddress
            dispatcher(delPayoutAddress({
                address: response.address
            }))

            setShow(false);
        }
    }, [discardAddressData.data, dispatcher]);

    const discard = useCallback(async () => {
        await discardAddress({
            variables: {
                dto: {
                    addressId: addressId,
                }
            }
        });
    }, [discardAddress, addressId]);

    return (
        <>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-80"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
            >
                <div className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"></div>
            </Transition>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-in-out duration-200"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in-out duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
            >
                <div
                    className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
                    <div onClick={(e) => e.stopPropagation()}
                         className="bg-white rounded shadow-lg overflow-auto max-w-lg w-full max-h-full">

                        {/* Modal header */}
                        <div className="p-4">
                            <div className="text-lg font-semibold text-slate-800">Delete from Whitelist</div>
                        </div>
                        <div className='divide-y'>
                            <div></div>
                            <div></div>
                        </div>
                        {/* Modal content */}
                        <div className="p-4 pb-0">
                            <div className="text-xs italic mb-6 text-center">
                                By submitting this form, you confirm that all
                                further payments to this address will only go through after manual confirmation.
                            </div>

                            <div className="text-sm font-bold text-center mb-4">{payoutAddress?.address}</div>
                        </div>
                        {/* Modal footer */}

                        <div className="flex justify-end p-4">
                            <button onClick={() => setShow(false)}
                                    className='bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2'>
                                Cancel
                            </button>
                            <button
                                onClick={discard}
                                className={`${discardAddressData.loading && 'opacity-40'} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}>
                                {discardAddressData.loading
                                    ? <svg className="animate-spin w-4 h-4 fill-current shrink-0 mr-2" viewBox="0 0 16 16">
                                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                    </svg>
                                    : ''
                                }
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </>
    );
};

export default DeletePayoutAddressModal;
